import { useState } from "react";
import { http } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Prohibit, Warning } from "@phosphor-icons/react";

// Duration picker — 0 means kick-only (no re-auth block); -1 means also suspend.
const OPTIONS = [
  { value: "0",     label: "0s — Kick only (client may redial in ~1s)" },
  { value: "60",    label: "60 s — Short block (troubleshooting)" },
  { value: "300",   label: "5 min — Medium block" },
  { value: "-1",    label: "Until reactivated — Suspend RADIUS auth" },
];

/**
 * Reusable kick dialog with duration picker. Backed by /coa/disconnect?block_seconds=<n>
 * plus /suspend for the "Until reactivated" option. Owner passes `trigger`
 * (usually the icon button) as children.
 */
export default function KickDialog({ subscriberId, username, onDone, children, defaultDuration = "60" }) {
  const [open, setOpen] = useState(false);
  const [dur, setDur] = useState(defaultDuration);
  const [busy, setBusy] = useState(false);

  const fire = async () => {
    setBusy(true);
    try {
      let data;
      if (dur === "-1") {
        // Suspend also triggers CoA internally; keep the envelope shape roughly the same.
        const r = await http.post(`/subscribers/${subscriberId}/suspend`);
        data = { ok: true, coa: r.data?.coa || {}, suspended: true };
      } else {
        const r = await http.post(`/subscribers/${subscriberId}/coa/disconnect?block_seconds=${Number(dur)}`);
        data = r.data;
      }
      const coa = data?.coa || {};
      const rc = coa.radclient_status || "?";
      const api = coa.api_status || "not_attempted";
      const blk = data?.suspended
        ? "suspended"
        : (coa.block_until ? `blocked ${Number(dur)}s` : "no block");
      const detail = `CoA:${rc} · API:${api} · ${blk}`;
      if (data?.ok) toast.success(`Kicked ${username} — ${detail}`);
      else toast.error(`Not disconnected: ${username} — ${detail}`, { duration: 8000 });
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <div onClick={() => setOpen(true)} className="inline-flex">{children}</div>
      <DialogContent className="max-w-md" data-testid="kick-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Prohibit size={18} className="text-destructive" /> Kick {username}</DialogTitle>
          <DialogDescription>
            RADIUS Disconnect via CoA. Pick a re-auth block window — otherwise the CPE will auto-redial within a second and appear online again.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label className="uppercase-label">Block re-auth for</Label>
          <Select value={dur} onValueChange={setDur}>
            <SelectTrigger data-testid="kick-duration"><SelectValue /></SelectTrigger>
            <SelectContent>
              {OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {dur === "-1" && (
            <div className="flex items-start gap-2 text-[11px] text-warning bg-warning/10 border border-warning/30 p-2 rounded-sm">
              <Warning size={14} className="mt-0.5 shrink-0" />
              Suspend sets <span className="font-mono">status=suspended</span>. You'll need to manually Reactivate the subscriber to bring them back online.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={fire} disabled={busy} className="rounded-sm bg-destructive hover:bg-destructive/90 text-destructive-foreground" data-testid="kick-confirm">
            {busy ? "Sending…" : "Kick now"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
